// MINIMAL UPDATE: src/services/productRequestService.js - Just add real-time listener
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot, // ADD THIS
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

export const getProductRequests = async (vendorId) => {
  try {
    console.log('Getting product requests for vendor:', vendorId);
    
    const requestsRef = collection(db, 'productRequests');
    const q = query(
      requestsRef,
      where('vendorId', '==', vendorId),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const requests = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      console.log('Found request:', doc.id, data);
      
      requests.push({
        id: doc.id,
        ...data
      });
    });
    
    console.log(`Found ${requests.length} product requests for vendor ${vendorId}`);
    return requests;
  } catch (error) {
    console.error('Error getting product requests:', error);
    
    // If orderBy fails, try without it
    try {
      const requestsRef = collection(db, 'productRequests');
      const q = query(
        requestsRef,
        where('vendorId', '==', vendorId)
      );
      
      const querySnapshot = await getDocs(q);
      const requests = [];
      
      querySnapshot.forEach((doc) => {
        requests.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Sort manually by creation date (newest first)
      requests.sort((a, b) => {
        const dateA = a.createdAt?.toDate() || new Date(0);
        const dateB = b.createdAt?.toDate() || new Date(0);
        return dateB.getTime() - dateA.getTime();
      });
      
      console.log(`Found ${requests.length} product requests (fallback query)`);
      return requests;
    } catch (fallbackError) {
      console.error('Fallback query also failed:', fallbackError);
      throw fallbackError;
    }
  }
};

// ADD THIS NEW FUNCTION - Real-time listener for product requests
export const subscribeToProductRequests = (vendorId, callback) => {
  try {
    console.log('🔄 Setting up real-time product requests listener for vendor:', vendorId);
    
    const requestsRef = collection(db, 'productRequests');
    const q = query(
      requestsRef,
      where('vendorId', '==', vendorId),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('📦 Real-time update: product requests changed, size:', snapshot.size);
      
      const requests = [];
      snapshot.forEach((doc) => {
        requests.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Call the callback with updated requests
      callback(requests);
      
    }, (error) => {
      console.error('❌ Error in real-time listener:', error);
      // Fallback - try without orderBy
      const fallbackQ = query(requestsRef, where('vendorId', '==', vendorId));
      return onSnapshot(fallbackQ, (snapshot) => {
        const requests = [];
        snapshot.forEach((doc) => {
          requests.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Sort manually
        requests.sort((a, b) => {
          const dateA = a.createdAt?.toDate() || new Date(0);
          const dateB = b.createdAt?.toDate() || new Date(0);
          return dateB.getTime() - dateA.getTime();
        });
        
        callback(requests);
      });
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up real-time listener:', error);
    return null;
  }
};

export const respondToProductRequest = async (requestId, action, quantity = 0) => {
  try {
    console.log('Responding to request:', requestId, action, quantity);
    
    const requestRef = doc(db, 'productRequests', requestId);
    
    // Get current request data
    const requestSnap = await getDoc(requestRef);
    if (!requestSnap.exists()) {
      throw new Error('Request not found');
    }
    
    const requestData = requestSnap.data();
    console.log('Current request data:', requestData);
    
    const updateData = {
      status: action === 'accept' ? 'accepted' : 'rejected',
      updatedAt: serverTimestamp(),
      respondedAt: serverTimestamp()
    };
    
    if (action === 'accept' && quantity > 0) {
      updateData.approvedQuantity = quantity;
    }
    
    console.log('Updating request with:', updateData);
    await updateDoc(requestRef, updateData);
    
    // Update broadcast statistics if broadcast ID exists
    if (requestData.broadcastId) {
      try {
        console.log('Updating broadcast stats for:', requestData.broadcastId);
        const broadcastRef = doc(db, 'productBroadcasts', requestData.broadcastId);
        const broadcastSnap = await getDoc(broadcastRef);
        
        if (broadcastSnap.exists()) {
          const currentData = broadcastSnap.data();
          await updateDoc(broadcastRef, {
            responses: (currentData.responses || 0) + 1,
            pendingResponses: Math.max(0, (currentData.pendingResponses || 1) - 1),
            updatedAt: serverTimestamp()
          });
          console.log('Broadcast stats updated successfully');
        }
      } catch (broadcastError) {
        console.warn('Could not update broadcast stats:', broadcastError);
      }
    }
    
    console.log('Response submitted successfully');
    return { success: true };
  } catch (error) {
    console.error('Error responding to product request:', error);
    throw error;
  }
};

export const getPendingRequestsCount = async (vendorId) => {
  try {
    console.log('Getting pending requests count for vendor:', vendorId);
    
    const requestsRef = collection(db, 'productRequests');
    const q = query(
      requestsRef,
      where('vendorId', '==', vendorId),
      where('status', '==', 'pending')
    );
    
    const querySnapshot = await getDocs(q);
    
    // Filter out expired requests
    const now = new Date();
    let count = 0;
    
    querySnapshot.forEach((doc) => {
      const requestData = doc.data();
      
      // Check if request has expiry and if it's still valid
      if (requestData.expiresAt) {
        const expiryDate = requestData.expiresAt.toDate ? requestData.expiresAt.toDate() : new Date(requestData.expiresAt);
        if (now <= expiryDate) {
          count++;
        }
      } else {
        // If no expiry date, count it as pending
        count++;
      }
    });
    
    console.log(`Found ${count} pending requests for vendor ${vendorId}`);
    return count;
  } catch (error) {
    console.error('Error getting pending requests count:', error);
    return 0;
  }
};

export const getProductRequestStatistics = async (vendorId) => {
  try {
    console.log('Getting product request statistics for vendor:', vendorId);
    
    const requestsRef = collection(db, 'productRequests');
    const q = query(
      requestsRef,
      where('vendorId', '==', vendorId)
    );
    
    const querySnapshot = await getDocs(q);
    
    let totalRequests = 0;
    let pendingRequests = 0;
    let acceptedRequests = 0;
    let rejectedRequests = 0;
    let expiredRequests = 0;
    
    const now = new Date();
    
    querySnapshot.forEach((doc) => {
      const requestData = doc.data();
      totalRequests++;
      
      // Check if expired
      const isExpired = requestData.expiresAt && 
        now > (requestData.expiresAt.toDate ? requestData.expiresAt.toDate() : new Date(requestData.expiresAt));
      
      if (isExpired) {
        expiredRequests++;
      } else {
        switch (requestData.status) {
          case 'pending':
            pendingRequests++;
            break;
          case 'accepted':
            acceptedRequests++;
            break;
          case 'rejected':
            rejectedRequests++;
            break;
        }
      }
    });
    
    const stats = {
      totalRequests,
      pendingRequests,
      acceptedRequests,
      rejectedRequests,
      expiredRequests
    };
    
    console.log('Product request statistics:', stats);
    return stats;
  } catch (error) {
    console.error('Error getting product request statistics:', error);
    return {
      totalRequests: 0,
      pendingRequests: 0,
      acceptedRequests: 0,
      rejectedRequests: 0,
      expiredRequests: 0
    };
  }
};

// Real-time listener function - ADD THIS TO YOUR EXISTING SERVICE
export const subscribeToProductRequestsRealTime = (vendorId, callback) => {
  try {
    console.log('🔄 Setting up real-time product requests listener for vendor:', vendorId);
    
    const requestsRef = collection(db, 'productRequests');
    const q = query(
      requestsRef,
      where('vendorId', '==', vendorId),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      console.log('📦 Real-time update: product requests changed, size:', snapshot.size);
      
      const requests = [];
      snapshot.forEach((doc) => {
        requests.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Call the callback with updated requests
      callback(requests);
      
    }, (error) => {
      console.error('❌ Error in real-time listener:', error);
      // Fallback - try without orderBy
      const fallbackQ = query(requestsRef, where('vendorId', '==', vendorId));
      return onSnapshot(fallbackQ, (snapshot) => {
        const requests = [];
        snapshot.forEach((doc) => {
          requests.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Sort manually
        requests.sort((a, b) => {
          const dateA = a.createdAt?.toDate() || new Date(0);
          const dateB = b.createdAt?.toDate() || new Date(0);
          return dateB.getTime() - dateA.getTime();
        });
        
        callback(requests);
      });
    });
    
    return unsubscribe;
  } catch (error) {
    console.error('Error setting up real-time listener:', error);
    return null;
  }
};